import { baseApi } from "./baseApi";

const brokerCompanyApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getBrokerCompanies: builder.query({
      query: () => ({
        url: "/broker-companies",
        method: "GET",
      }),
      providesTags: ["BrokerCompanies"],
    }),

    getBrokerCompany: builder.query({
      query: (id) => ({
        url: `/broker-companies/${id}`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "BrokerCompany", id }],
    }),

    /** Loan partner-broker company NMLS → authorized broker row (pricing / compensation contexts). */
    getBrokerCompanyByNmls: builder.query({
      query: (companyNmls) => ({
        url: "/broker-companies/by-nmls",
        method: "GET",
        params: { companyNmls },
      }),
      providesTags: (result, error, companyNmls) => [{ type: "BrokerCompanyByNmls", id: companyNmls }],
    }),

    getBrokerCompanyOffices: builder.query({
      query: (id) => ({
        url: `/broker-companies/${id}/offices`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "BrokerCompany", id: `${id}-offices` }],
    }),

    createBrokerCompany: builder.mutation({
      query: (body) => ({
        url: "/broker-companies",
        method: "POST",
        body,
      }),
      invalidatesTags: ["BrokerCompanies"],
    }),

    updateBrokerCompany: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/broker-companies/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { id }) => [
        "BrokerCompanies",
        { type: "BrokerCompany", id },
      ],
    }),

    activateBrokerCompany: builder.mutation({
      query: (id) => ({
        url: `/broker-companies/${id}/activate`,
        method: "PUT",
      }),
      invalidatesTags: (result, error, id) => [
        "BrokerCompanies",
        { type: "BrokerCompany", id },
      ],
    }),

    disableBrokerCompany: builder.mutation({
      query: (id) => ({
        url: `/broker-companies/${id}/disable`,
        method: "PUT",
      }),
      invalidatesTags: (result, error, id) => [
        "BrokerCompanies",
        { type: "BrokerCompany", id },
      ],
    }),

    preApproveBrokerCompany: builder.mutation({
      query: ({ id, conflictAction, existingFileContactId } = {}) => ({
        url: `/broker-companies/${id}/pre-approve`,
        method: "PUT",
        body: conflictAction != null ? { conflictAction, existingFileContactId } : undefined,
      }),
      invalidatesTags: (result, error, { id }) => [
        "BrokerCompanies",
        { type: "BrokerCompany", id },
      ],
    }),

    deleteBrokerCompany: builder.mutation({
      query: (id) => ({
        url: `/broker-companies/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["BrokerCompanies"],
    }),

    getBrokerCompanyUsers: builder.query({
      query: (id) => ({
        url: `/broker-companies/${id}/users`,
        method: "GET",
      }),
      providesTags: (result, error, id) => [{ type: "BrokerCompany", id: `${id}-users` }],
    }),

    inviteBrokerUser: builder.mutation({
      query: (body) => ({
        url: "/users/invite",
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { brokerCompanyId }) => [
        { type: "BrokerCompany", id: `${brokerCompanyId}-users` },
        "MyCompanyUsers",
      ],
    }),

    approveUser: builder.mutation({
      query: ({ userId, brokerCompanyId, ...body }) => ({
        url: `/users/${userId}/approve`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { brokerCompanyId }) => [
        { type: "BrokerCompany", id: `${brokerCompanyId}-users` },
      ],
    }),

    editUser: builder.mutation({
      query: ({ userId, brokerCompanyId, ...body }) => ({
        url: `/users/${userId}/edit`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { brokerCompanyId }) => [
        { type: "BrokerCompany", id: `${brokerCompanyId}-users` },
        "MyCompanyUsers",
      ],
    }),

    deleteUser: builder.mutation({
      query: ({ userId }) => ({
        url: `/users/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { brokerCompanyId }) => [
        { type: "BrokerCompany", id: `${brokerCompanyId}-users` },
        "MyCompanyUsers",
      ],
    }),

    resendConfirmation: builder.mutation({
      query: ({ userId }) => ({
        url: `/users/${userId}/resend-confirmation`,
        method: "POST",
      }),
    }),

    editOffice: builder.mutation({
      query: ({ officeId, brokerCompanyId, ...body }) => ({
        url: `/broker-companies/${officeId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { brokerCompanyId }) => [
        { type: "BrokerCompany", id: `${brokerCompanyId}-offices` },
        "MyCompanyOffices",
      ],
    }),

    approveOffice: builder.mutation({
      query: ({ officeId, brokerCompanyId, ...body }) => ({
        url: `/broker-companies/${officeId}/approve`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { brokerCompanyId }) => [
        { type: "BrokerCompany", id: `${brokerCompanyId}-offices` },
      ],
    }),

    // BrokerAdmin my-company endpoints
    getMyCompany: builder.query({
      query: () => ({
        url: "/broker-companies/my-company",
        method: "GET",
      }),
      providesTags: ["MyCompany"],
    }),

    getMyCompanyOffices: builder.query({
      query: () => ({
        url: "/broker-companies/my-company/offices",
        method: "GET",
      }),
      providesTags: ["MyCompanyOffices"],
    }),

    getMyCompanyUsers: builder.query({
      query: () => ({
        url: "/broker-companies/my-company/users",
        method: "GET",
      }),
      providesTags: ["MyCompanyUsers"],
    }),

    updateMyCompany: builder.mutation({
      query: (body) => ({
        url: "/broker-companies/my-company",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["MyCompany"],
    }),

    addSatelliteOffice: builder.mutation({
      query: (body) => ({
        url: "/broker-companies/my-company/offices",
        method: "POST",
        body,
      }),
      invalidatesTags: ["MyCompanyOffices"],
    }),

    brokerSignup: builder.mutation({
      query: (body) => ({
        url: "/broker-companies/signup",
        method: "POST",
        body,
      }),
    }),

    getAccountExecutives: builder.query({
      query: () => ({
        url: "/broker-companies/account-executives",
        method: "GET",
      }),
      providesTags: ["AccountExecutives"],
    }),

    getMyAssignedBrokerCompanies: builder.query({
      query: () => ({
        url: "/broker-companies/my-assigned",
        method: "GET",
      }),
      providesTags: ["MyAssignedBrokerCompanies"],
    }),

  }),
});

export default brokerCompanyApi;
